import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { Key, Users, ChartBar, ArrowLeft, ShieldStar, FloppyDisk, Package, SignOut, Coins, ChartLineUp, ShieldWarning, Translate, Globe, Scales, Lightning, Ticket, Handshake, ChartPieSlice, Export } from "@phosphor-icons/react";
import { useAuth } from "@/App";

export const Field = ({ label, value, onChange, placeholder, testId, type = "text" }) => (
  <div>
    <label className="text-xs font-medium text-gray-600 block mb-1">{label}</label>
    <input data-testid={testId} type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder={placeholder}
      className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-500" />
  </div>
);

export const Section = ({ icon: Icon, title, subtitle, children, onSave, saveTestId }) => (
  <div className="border border-gray-200 rounded-md p-5 mb-5">
    <div className="flex items-center gap-2 mb-1"><Icon size={18} className="text-blue-600" /><h2 className="font-heading font-bold text-gray-900">{title}</h2></div>
    <p className="text-xs text-gray-500 mb-4">{subtitle}</p>
    <div className="grid grid-cols-2 gap-3">{children}</div>
    {onSave && (
      <button data-testid={saveTestId} onClick={onSave}
        className="mt-4 flex items-center gap-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm font-semibold rounded-md px-4 py-2 transition-colors">
        <FloppyDisk size={14} /> Save
      </button>
    )}
  </div>
);

const NAV = [
  { path: "/admin/keys", label: "Integration Keys", icon: Key, testId: "admin-nav-keys" },
  { path: "/admin/packages", label: "Packages", icon: Package, testId: "admin-nav-packages" },
  { path: "/admin/pricing", label: "Pricing & Tokens", icon: Coins, testId: "admin-nav-pricing" },
  { path: "/admin/economics", label: "Economics", icon: ChartLineUp, testId: "admin-nav-economics" },
  { path: "/admin/security", label: "Security", icon: ShieldWarning, testId: "admin-nav-security" },
  { path: "/admin/users", label: "Users & Teams", icon: Users, testId: "admin-nav-users" },
  { path: "/admin/stats", label: "Platform Stats", icon: ChartBar, testId: "admin-nav-stats" },
  { path: "/admin/priority", label: "Priority Inbox", icon: Lightning, testId: "admin-nav-priority" },
  { path: "/admin/codes", label: "Access Codes", icon: Ticket, testId: "admin-nav-codes" },
  { path: "/admin/distributors", label: "Distributors", icon: Handshake, testId: "admin-nav-distributors" },
  { path: "/admin/profit", label: "Profit Estimation", icon: ChartPieSlice, testId: "admin-nav-profit" },
  { path: "/admin/export", label: "Export DB", icon: Export, testId: "admin-nav-export" },
  { path: "/admin/languages", label: "System Languages", icon: Translate, testId: "admin-nav-languages" },
  { path: "/admin/legal", label: "Legal Pages", icon: Scales, testId: "admin-nav-legal" },
  { path: "/admin/website", label: "Website", icon: Globe, testId: "admin-nav-website" },
];

export const AdminShell = ({ title, subtitle, children, testId }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  if (user && user.role !== "superadmin") return <Navigate to="/app" replace />;

  return (
    <div className="h-screen flex overflow-hidden">
      <aside className="w-60 shrink-0 h-full bg-gray-900 text-gray-300 flex flex-col">
        <div className="px-4 py-5 border-b border-gray-800">
          <div className="flex items-center gap-2">
            <ShieldStar size={20} weight="fill" className="text-blue-400" />
            <span className="font-heading font-bold text-white">Super Admin</span>
          </div>
          <div className="text-xs text-gray-500 mt-0.5 truncate">{user?.email}</div>
        </div>
        <nav className="p-3 space-y-1 flex-1">
          {NAV.map((n) => {
            const active = location.pathname === n.path;
            return (
              <button key={n.path} data-testid={n.testId} onClick={() => navigate(n.path)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm transition-colors ${active ? "bg-blue-600 text-white font-semibold" : "hover:bg-gray-800 text-gray-300"}`}>
                <n.icon size={16} weight={active ? "fill" : "regular"} />
                {n.label}
              </button>
            );
          })}
        </nav>
        <div className="p-3 border-t border-gray-800">
          <button data-testid="admin-back-to-inbox" onClick={() => navigate("/app")}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-gray-800 transition-colors">
            <ArrowLeft size={16} /> Back to Inbox
          </button>
          <button data-testid="admin-logout-button" onClick={logout}
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-md text-sm hover:bg-gray-800 transition-colors">
            <SignOut size={16} /> Sign out
          </button>
        </div>
      </aside>
      <div className="flex-1 overflow-y-auto scroll-thin bg-white" data-testid={testId}>
        <div className="max-w-4xl px-8 py-8">
          <h1 className="font-heading text-2xl font-bold text-gray-900">{title}</h1>
          {subtitle && <p className="text-sm text-gray-500 mt-1 mb-6">{subtitle}</p>}
          {children}
        </div>
      </div>
    </div>
  );
};
